<script setup lang="ts">
import { computed, inject, ref, Ref, toRefs } from 'vue';
import RadioButton from 'primevue/radiobutton';
import useVuelidate from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import { useOrganizationContractsStore } from '@/store';
import { CONTRACT_ENUMS } from '@/enum';
import { ContractDetailInterface } from '@/types';

interface Props {
  contract: ContractDetailInterface;
  scope: string;
}

const contractStore = useOrganizationContractsStore();
const props = defineProps<Props>();
const { contract, scope } = toRefs(props);
const selectedMainForm = inject<Ref<CONTRACT_ENUMS.FORMS>>('selectedForm', ref(CONTRACT_ENUMS.FORMS.FLAT_FEE));
const canEdit = contractStore.canEditOrCreateContracts;

const noShowRatePercentage = computed<number>({
  get() {
    return contract.value.noshow_is_fixed ? 0 : (contract.value.noshow_rate as number);
  },
  set(value) {
    contract.value.noshow_rate = value;
  },
});

const noShowRateFlatFee = computed<number>({
  get() {
    return !contract.value.noshow_is_fixed ? 0 : (contract.value.noshow_rate as number);
  },
  set(value) {
    contract.value.noshow_rate = value;
  },
});

const rules = computed(() => {
  return {
    noshow_is_fixed: {
      required,
    },
    noshow_rate: {
      required: helpers.withMessage('Please provide the no show/late cancel rate or percentage', required),
    },
  };
});

const v$ = useVuelidate(rules, contract, { $scope: scope.value });
</script>

<template>
  <div
    class="p-3 w-full border-round-lg bg-gray-50 no-show-container"
    :class="{ flat: selectedMainForm !== CONTRACT_ENUMS.FORMS.HOURLY }"
  >
    <label class="required"><b>No Show/Late Cancel</b></label>
    <div class="flex align-items-center justify-content-between mx-0 mt-3 mb-1">
      <div class="radio-group flex flex-row align-items-center">
        <RadioButton
          v-model="contract.noshow_is_fixed"
          input-id="fee_percentage"
          :value="0"
          name="fee_percentage"
          class="mr-1 relative"
          :class="{ 'p-invalid': v$.noshow_is_fixed.$invalid && v$.noshow_is_fixed.$dirty }"
          :disabled="!canEdit"
          @update:model-value="() => (contract.noshow_rate = 0)"
        />
        <label class="m-1" for="fee_percentage">Percentage</label>
      </div>
      <InputNumber
        v-model="noShowRatePercentage"
        :max="100"
        :min="0"
        :max-fraction-digits="2"
        :min-fraction-digits="0"
        mode="decimal"
        suffix="%"
        :disabled="contract.noshow_is_fixed === 1 || !canEdit"
        input-class="border-none"
        :input-style="{ padding: '1px 7px' }"
        :class="{ 'p-invalid': v$.noshow_rate.$invalid && v$.noshow_rate.$dirty }"
        @blur="v$.noshow_rate.$touch()"
        @focus="(e: Event) => e?.target?.select()"
      />
    </div>
    <div class="flex flex-row align-items-center justify-content-between">
      <div class="radio-group flex flex-row align-items-center">
        <RadioButton
          v-model="contract.noshow_is_fixed"
          input-id="fee_flat_fee"
          :value="1"
          name="fee_flat"
          class="mr-1 relative"
          :class="{ 'p-invalid': v$.noshow_is_fixed.$invalid && v$.noshow_is_fixed.$dirty }"
          :disabled="!canEdit"
          @update:model-value="() => (contract.noshow_rate = 0)"
        />
        <label class="m-1" for="fee_flat_fee">Flat Fee</label>
      </div>
      <InputNumber
        v-model="noShowRateFlatFee"
        mode="currency"
        currency="USD"
        input-class="border-none"
        :input-style="{ padding: '1px 7px' }"
        :disabled="contract.noshow_is_fixed === 0 || !canEdit"
        :class="{ 'p-invalid': v$.noshow_rate.$invalid && v$.noshow_rate.$dirty }"
        @blur="v$.noshow_rate.$touch()"
        @focus="(e: Event) => e?.target?.select()"
      />
    </div>
    <div v-for="error of v$.$errors" :key="error.$uid" class="input-errors mt-2 text-align-left w-6">
      <div class="p-error">{{ error.$message }}</div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.no-show-container {
  height: 282px;

  :deep(.p-inputnumber-input) {
    width: 116px !important;
    height: 20px;
    font-size: 14px;
    line-height: 18px;
    border-radius: 6px;

    &:disabled {
      color: $grayMedium;
    }
  }
}

.flat {
  max-height: 122px;
  height: 122px;
}

:deep(.tab-menu-container) {
  .tab-menu-details {
    box-shadow: none;
  }
  .p-tabmenu {
    border: 2px solid $grayLight;
    border-radius: 4px;

    .p-tabmenuitem {
      width: 120px;

      .p-menuitem-link {
        color: $grayDark;
        font-weight: bold;
        font-size: 14px;
        line-height: 18px;
        width: 100%;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left-color: transparent;

        span {
          width: 100%;
          text-align: center;
        }
      }
      .p-menuitem-link:first-of-type {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right-color: transparent;
      }
    }
    & .p-highlight {
      background: var(--primary-color-light);
      box-shadow: none;

      .p-menuitem-link {
        box-shadow: none !important;
      }
    }
  }
  &::after {
    background: none;
  }
}
:deep(.p-inputnumber-input) {
  width: 100% !important;
  font-size: 14px;
  padding: 7px;
  height: 20px;
}

.form-container {
  top: -14px;

  table {
    thead {
      font-size: 10px;
      line-height: 12px;
      color: $grayDark;

      tr td {
        vertical-align: top;
      }
    }
    tbody {
      font-size: 14px;
      line-height: 18px;

      .switch {
        width: 47px;
        height: 24px;
      }

      .group-events-input {
        width: 73px;
        height: 20px;
      }
    }
  }
}
</style>
